import { useQuery } from '@tanstack/react-query'
import { ArrowLeftRight, BookCopy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { libraryStatsQuery, loansQuery } from '@/features/library/api'
import { LoanTable } from '@/features/library/components/LoanTable'
import { BORROWER_KINDS } from '@/features/library/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { humanize } from '@/utils/format'

const FILTERS = ['state', 'borrowerKind', 'fines'] as const

export function LoansPage() {
  const { t } = useTranslation('library')
  useDocumentTitle('Library loans')
  const { can, school } = useAuth()
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'dueOn' })
  const list = useQuery(loansQuery(query))
  const stats = useQuery(libraryStatsQuery())
  const tab = state.filters.fines === 'outstanding' ? 'fines' : (state.filters.state ?? 'all')
  const hasFilters = Boolean(state.search || state.filters.borrowerKind || tab !== 'all')

  return (
    <>
      <PageHeader
        title={t('Loans')}
        description={t('Every book lent out, with overdue flags and fines.')}
        breadcrumbs={[{ label: t('Library'), to: '/admin/library' }, { label: t('Loans') }]}
        homeHref="/admin"
        actions={
          <Can permission="library.manage">
            <Button asChild leftIcon={<ArrowLeftRight />}>
              <Link to="/admin/library/circulation">{t('Circulation desk')}</Link>
            </Button>
          </Can>
        }
      />
      <Card>
        <div className="px-4 pt-2">
          <Tabs
            value={tab}
            onValueChange={(v) =>
              update(
                v === 'fines'
                  ? { fines: 'outstanding', state: undefined }
                  : { state: v === 'all' ? undefined : v, fines: undefined },
              )
            }
          >
            <TabsList aria-label={t('Loan state')}>
              <TabsTrigger value="all">{t('All')}</TabsTrigger>
              <TabsTrigger value="active" count={stats.data?.onLoan}>
                {t('On loan')}
              </TabsTrigger>
              <TabsTrigger value="overdue" count={stats.data?.overdue}>
                {t('Overdue')}
              </TabsTrigger>
              <TabsTrigger value="returned">{t('Returned')}</TabsTrigger>
              <TabsTrigger value="fines">{t('Unpaid fines')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update({ search: undefined, borrowerKind: undefined, state: undefined, fines: undefined })
                }
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Book, ISBN, borrower or code')}
            label={t('Search loans')}
          />
          <Select
            aria-label={t('Filter by borrower type')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('All borrowers')}
            value={state.filters.borrowerKind ?? ''}
            onChange={(e) => update({ borrowerKind: e.target.value })}
            options={BORROWER_KINDS.map((k) => ({ value: k, label: humanize(k) }))}
          />
        </FilterBar>
        <LoanTable
          caption={t('Library loans')}
          loans={list.data?.data}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          currency={school?.currency ?? 'USD'}
          actions={can('library.manage')}
          bookHref={(l) => `/admin/library/books/${l.book.id}`}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          empty={
            <EmptyState
              icon={<BookCopy />}
              title={hasFilters ? t('No loans match') : t('No loans yet')}
              description={
                hasFilters
                  ? t('Try another search or tab.')
                  : t('Books issued at the circulation desk will appear here.')
              }
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>
    </>
  )
}
